import React, { useState, useEffect } from "react";
import { db } from "../../firebase";
import { updateDoc, doc } from "firebase/firestore";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

const AccommodationTab = ({ trip, theme, onUpdateTrip, isDarkMode }) => {
  const [editingId, setEditingId] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [forceRender, setForceRender] = useState(0);

  const [activeMapQuery, setActiveMapQuery] = useState(trip.name || "Japan");

  const initialFormState = {
    name: "",
    checkInDate: trip.start || "",
    checkInTime: "14:00",
    checkOutDate: trip.end || "",
    checkOutTime: "11:00",
    bookingRef: "",
    accessCode: "", // รหัสเข้าพัก
    notes: "",
    price: "",
    totalPrice: "",
    link: "",
    googleMap: "",
    checkInLink: "",
    guideLink: "", // คู่มือที่พัก
    agency: "", // 🌟 ช่องทางจอง
    phone: "", // 🌟 เบอร์ที่พัก
    email: "", // 🌟 อีเมลที่พัก
  };

  const [formData, setFormData] = useState(initialFormState);
  const [nights, setNights] = useState(0);

  // คำนวณจำนวนคืนเมื่อมีการเปลี่ยนวันที่
  useEffect(() => {
    if (formData.checkInDate && formData.checkOutDate) {
      const inDate = new Date(formData.checkInDate);
      const outDate = new Date(formData.checkOutDate);

      const diffTime = outDate.getTime() - inDate.getTime();
      let diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays < 0) diffDays = 0;
      setNights(diffDays);

      // เมื่อเปลี่ยนวันที่ ให้ยึด "ราคาต่อคืน" เป็นหลักในการคำนวณราคารวมใหม่
      setFormData((prev) => {
        const currentPrice = parseFloat(prev.price || 0);
        return {
          ...prev,
          totalPrice: diffDays > 0 ? currentPrice * diffDays : currentPrice,
        };
      });
    } else {
      setNights(0);
    }
  }, [formData.checkInDate, formData.checkOutDate]);

  const handleTimeInput = (e, field) => {
    let val = e.target.value.replace(/[^0-9]/g, "");
    if (val.length >= 3) {
      val = val.slice(0, 2) + ":" + val.slice(2, 4);
    }
    setFormData({ ...formData, [field]: val });
  };

  const handlePriceChange = (e) => {
    const val = e.target.value;
    const p = parseFloat(val || 0);
    setFormData((prev) => ({
      ...prev,
      price: val,
      totalPrice: nights > 0 ? p * nights : p,
    }));
  };

  const handleTotalPriceChange = (e) => {
    const val = e.target.value;
    const tp = parseFloat(val || 0);
    setFormData((prev) => ({
      ...prev,
      totalPrice: val,
      price: nights > 0 ? Number((tp / nights).toFixed(2)) : tp,
    }));
  };

  const formatDisplayDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("th-TH", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  const handleEditClick = (acc) => {
    setFormData({
      ...initialFormState,
      ...acc,
    });
    setEditingId(acc.id);
    setIsAdding(false);
  };

  const handleCancel = () => {
    setEditingId(null);
    setIsAdding(false);
    setFormData(initialFormState);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const accData = {
        ...formData,
        id: editingId || `acc_${Date.now()}`,
        price: parseFloat(formData.price || 0),
        totalPrice: parseFloat(formData.totalPrice || 0),
        order: editingId
          ? trip.accommodations?.find((a) => a.id === editingId)?.order
          : trip.accommodations?.length || 0,
      };

      let newList;
      if (editingId) {
        newList = (trip.accommodations || []).map((a) =>
          a.id === editingId ? accData : a
        );
      } else {
        newList = [...(trip.accommodations || []), accData];
      }

      await updateDoc(doc(db, "trips", trip.id), { accommodations: newList });
      onUpdateTrip(newList, "accommodations");

      setActiveMapQuery(formData.name);

      setForceRender((prev) => prev + 1);
      handleCancel();
    } catch (error) {
      alert("เกิดข้อผิดพลาดในการบันทึกข้อมูล");
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("คุณต้องการลบที่พักนี้ใช่หรือไม่?")) {
      const newList = (trip.accommodations || []).filter((a) => a.id !== id);
      await updateDoc(doc(db, "trips", trip.id), { accommodations: newList });
      onUpdateTrip(newList, "accommodations");
      setForceRender((prev) => prev + 1);
    }
  };

  const onDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(sortedAccs);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const updatedItems = items.map((item, index) => ({
      ...item,
      order: index,
    }));

    try {
      await updateDoc(doc(db, "trips", trip.id), {
        accommodations: updatedItems,
      });
      onUpdateTrip(updatedItems, "accommodations");
      setForceRender((prev) => prev + 1);
    } catch (error) {
      console.error("Error updating accommodation order:", error);
    }
  };

  const sortedAccs = [...(trip.accommodations || [])].sort((a, b) => {
    if (typeof a.order === "number" && typeof b.order === "number") {
      return a.order - b.order;
    }
    return new Date(a.checkInDate) - new Date(b.checkInDate);
  });

  const inputStyle = {
    padding: "10px 12px",
    borderRadius: "8px",
    border: `1px solid ${isDarkMode ? "#444" : "#ddd"}`,
    backgroundColor: isDarkMode ? "#2c2c2c" : "#fff",
    color: isDarkMode ? "#fff" : "#000",
    fontSize: "14px",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  };

  const labelStyle = {
    fontSize: "12px",
    fontWeight: "bold",
    color: isDarkMode ? "#aaa" : "#666",
    marginBottom: "5px",
    display: "block",
  };

  const formBgStyle = {
    backgroundColor: isDarkMode ? "#1e1e1e" : "#f8f9fa",
    borderRadius: "12px",
    padding: "20px",
    border: `1px solid ${theme?.border || (isDarkMode ? "#333" : "#ddd")}`,
    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
    marginBottom: "15px",
  };

  return (
    <div className="plan-split-view">
      <div className="plan-left-pane" style={{ paddingBottom: "30px" }}>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="acc-list">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "15px",
                }}
              >
                {sortedAccs.map((acc, index) => {
                  const isEditingThis = editingId === acc.id;
                  const isActive = activeMapQuery === acc.name;

                  return (
                    <Draggable
                      key={String(acc.id)}
                      draggableId={String(acc.id)}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          style={{ ...provided.draggableProps.style }}
                        >
                          {isEditingThis ? (
                            <div style={formBgStyle} className="no-print">
                              <form onSubmit={handleSave}>
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    marginBottom: "15px",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: "10px",
                                      flex: 1,
                                    }}
                                  >
                                    <input
                                      type="text"
                                      placeholder="ชื่อที่พัก (เช่น APA Hotel Ueno)"
                                      value={formData.name}
                                      onChange={(e) =>
                                        setFormData({
                                          ...formData,
                                          name: e.target.value,
                                        })
                                      }
                                      style={{
                                        ...inputStyle,
                                        fontWeight: "bold",
                                        fontSize: "16px",
                                      }}
                                      required
                                    />
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => handleDelete(acc.id)}
                                    style={{
                                      background: "none",
                                      border: "none",
                                      cursor: "pointer",
                                      fontSize: "18px",
                                      color: "#e74c3c",
                                      padding: "0 10px",
                                    }}
                                  >
                                    🗑️
                                  </button>
                                </div>

                                <div
                                  style={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: "20px",
                                  }}
                                >
                                  <div
                                    style={{
                                      flex: "2 1 400px",
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: "15px",
                                    }}
                                  >
                                    <div>
                                      <label style={labelStyle}>
                                        วันที่ Check-in
                                      </label>
                                      <div
                                        style={{
                                          display: "flex",
                                          gap: "8px",
                                          alignItems: "center",
                                        }}
                                      >
                                        <input
                                          type="date"
                                          value={formData.checkInDate}
                                          onChange={(e) =>
                                            setFormData({
                                              ...formData,
                                              checkInDate: e.target.value,
                                            })
                                          }
                                          style={{ ...inputStyle, flex: 1 }}
                                          required
                                        />
                                        <input
                                          type="text"
                                          placeholder="14:00"
                                          maxLength="5"
                                          value={formData.checkInTime}
                                          onChange={(e) =>
                                            handleTimeInput(e, "checkInTime")
                                          }
                                          style={{
                                            ...inputStyle,
                                            width: "80px",
                                            textAlign: "center",
                                          }}
                                        />
                                      </div>
                                    </div>

                                    <div>
                                      <label style={labelStyle}>
                                        วันที่ Check-out
                                      </label>
                                      <div
                                        style={{
                                          display: "flex",
                                          gap: "8px",
                                          alignItems: "center",
                                        }}
                                      >
                                        <input
                                          type="date"
                                          value={formData.checkOutDate}
                                          onChange={(e) =>
                                            setFormData({
                                              ...formData,
                                              checkOutDate: e.target.value,
                                            })
                                          }
                                          style={{ ...inputStyle, flex: 1 }}
                                          required
                                        />
                                        <input
                                          type="text"
                                          placeholder="11:00"
                                          maxLength="5"
                                          value={formData.checkOutTime}
                                          onChange={(e) =>
                                            handleTimeInput(e, "checkOutTime")
                                          }
                                          style={{
                                            ...inputStyle,
                                            width: "80px",
                                            textAlign: "center",
                                          }}
                                        />
                                      </div>
                                    </div>

                                    <div
                                      style={{
                                        padding: "10px",
                                        backgroundColor: isDarkMode
                                          ? "#2c2c2c"
                                          : "#eef2f3",
                                        borderRadius: "8px",
                                        display: "flex",
                                        gap: "15px",
                                        alignItems: "center",
                                      }}
                                    >
                                      <div style={{ flex: 1 }}>
                                        <label style={labelStyle}>
                                          ราคาต่อคืน (฿)
                                        </label>
                                        <input
                                          type="number"
                                          step="any"
                                          value={formData.price}
                                          onChange={handlePriceChange}
                                          style={inputStyle}
                                        />
                                      </div>
                                      <div
                                        style={{
                                          fontSize: "16px",
                                          fontWeight: "bold",
                                          color: "#888",
                                          marginTop: "15px",
                                        }}
                                      >
                                        x {nights} คืน =
                                      </div>
                                      <div style={{ flex: 1 }}>
                                        <label style={labelStyle}>
                                          ราคารวมทั้งหมด (฿)
                                        </label>
                                        <input
                                          type="number"
                                          step="any"
                                          value={formData.totalPrice}
                                          onChange={handleTotalPriceChange}
                                          style={{
                                            ...inputStyle,
                                            color: isDarkMode
                                              ? "#1dd1a1"
                                              : "#0984e3",
                                            fontWeight: "bold",
                                          }}
                                        />
                                      </div>
                                    </div>
                                  </div>

                                  <div
                                    style={{
                                      flex: "1 1 250px",
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: "10px",
                                      borderLeft: `1px solid ${
                                        isDarkMode ? "#444" : "#ddd"
                                      }`,
                                      paddingLeft: "20px",
                                    }}
                                  >
                                    <div>
                                      <label style={labelStyle}>
                                        หมายเลขยืนยัน (Booking Ref)
                                      </label>
                                      <input
                                        type="text"
                                        value={formData.bookingRef}
                                        onChange={(e) =>
                                          setFormData({
                                            ...formData,
                                            bookingRef: e.target.value,
                                          })
                                        }
                                        style={inputStyle}
                                      />
                                    </div>

                                    {/* 🌟 เพิ่ม Agency / เบอร์ / อีเมล ตรงนี้ */}
                                    <div>
                                      <label style={labelStyle}>
                                        🏢 ช่องทางจอง (Agency)
                                      </label>
                                      <input
                                        type="text"
                                        placeholder="เช่น Agoda, Trip.com..."
                                        value={formData.agency}
                                        onChange={(e) =>
                                          setFormData({
                                            ...formData,
                                            agency: e.target.value,
                                          })
                                        }
                                        style={inputStyle}
                                      />
                                    </div>
                                    <div
                                      style={{ display: "flex", gap: "10px" }}
                                    >
                                      <div style={{ flex: 1 }}>
                                        <label style={labelStyle}>
                                          📞 เบอร์โทรที่พัก
                                        </label>
                                        <input
                                          type="text"
                                          value={formData.phone}
                                          onChange={(e) =>
                                            setFormData({
                                              ...formData,
                                              phone: e.target.value,
                                            })
                                          }
                                          style={inputStyle}
                                        />
                                      </div>
                                      <div style={{ flex: 1 }}>
                                        <label style={labelStyle}>
                                          📧 อีเมลที่พัก
                                        </label>
                                        <input
                                          type="email"
                                          value={formData.email}
                                          onChange={(e) =>
                                            setFormData({
                                              ...formData,
                                              email: e.target.value,
                                            })
                                          }
                                          style={inputStyle}
                                        />
                                      </div>
                                    </div>
                                    {/* ------------------------------------- */}

                                    <div>
                                      <label style={labelStyle}>
                                        🔑 รหัสเข้าพัก (Access Code)
                                      </label>
                                      <input
                                        type="text"
                                        value={formData.accessCode}
                                        onChange={(e) =>
                                          setFormData({
                                            ...formData,
                                            accessCode: e.target.value,
                                          })
                                        }
                                        style={inputStyle}
                                      />
                                    </div>
                                    <div>
                                      <label style={labelStyle}>
                                        บันทึกเพิ่มเติม
                                      </label>
                                      <input
                                        type="text"
                                        value={formData.notes}
                                        onChange={(e) =>
                                          setFormData({
                                            ...formData,
                                            notes: e.target.value,
                                          })
                                        }
                                        style={inputStyle}
                                      />
                                    </div>
                                    <div>
                                      <label style={labelStyle}>
                                        🔗 ลิงก์จองที่พัก (Booking Link)
                                      </label>
                                      <input
                                        type="text"
                                        placeholder="วางลิงก์ที่นี่..."
                                        value={formData.link}
                                        onChange={(e) =>
                                          setFormData({
                                            ...formData,
                                            link: e.target.value,
                                          })
                                        }
                                        style={inputStyle}
                                      />
                                    </div>
                                    <div>
                                      <label style={labelStyle}>
                                        📍 Google Map Link
                                      </label>
                                      <input
                                        type="text"
                                        placeholder="วางลิงก์ที่นี่..."
                                        value={formData.googleMap}
                                        onChange={(e) =>
                                          setFormData({
                                            ...formData,
                                            googleMap: e.target.value,
                                          })
                                        }
                                        style={inputStyle}
                                      />
                                    </div>
                                    <div>
                                      <label style={labelStyle}>
                                        📲 Web Check-in / E-Ticket
                                      </label>
                                      <input
                                        type="text"
                                        placeholder="วางลิงก์ที่นี่..."
                                        value={formData.checkInLink}
                                        onChange={(e) =>
                                          setFormData({
                                            ...formData,
                                            checkInLink: e.target.value,
                                          })
                                        }
                                        style={inputStyle}
                                      />
                                    </div>
                                    <div>
                                      <label style={labelStyle}>
                                        📖 คู่มือที่พัก (Guide Link)
                                      </label>
                                      <input
                                        type="text"
                                        placeholder="วางลิงก์ที่นี่..."
                                        value={formData.guideLink}
                                        onChange={(e) =>
                                          setFormData({
                                            ...formData,
                                            guideLink: e.target.value,
                                          })
                                        }
                                        style={inputStyle}
                                      />
                                    </div>
                                  </div>
                                </div>

                                <div
                                  style={{
                                    display: "flex",
                                    gap: "10px",
                                    marginTop: "20px",
                                    justifyContent: "flex-end",
                                  }}
                                >
                                  <button
                                    type="button"
                                    onClick={handleCancel}
                                    style={{
                                      padding: "8px 16px",
                                      borderRadius: "8px",
                                      border: `1px solid ${
                                        isDarkMode ? "#555" : "#ccc"
                                      }`,
                                      background: isDarkMode ? "#333" : "white",
                                      color: isDarkMode ? "#fff" : "#000",
                                      cursor: "pointer",
                                    }}
                                  >
                                    ยกเลิก
                                  </button>
                                  <button
                                    type="submit"
                                    style={{
                                      padding: "8px 24px",
                                      borderRadius: "8px",
                                      border: "none",
                                      background: "#3b82f6",
                                      color: "white",
                                      fontWeight: "bold",
                                      cursor: "pointer",
                                    }}
                                  >
                                    บันทึก
                                  </button>
                                </div>
                              </form>
                            </div>
                          ) : (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: "15px",
                                opacity: snapshot.isDragging ? 0.8 : 1,
                                pageBreakInside: "avoid",
                              }}
                              onClick={() => setActiveMapQuery(acc.name)}
                            >
                              <div
                                className="no-print"
                                {...provided.dragHandleProps}
                                style={{
                                  color: "#bbb",
                                  marginTop: "15px",
                                  cursor: "grab",
                                }}
                              >
                                ⋮⋮
                              </div>

                              <div
                                style={{
                                  flex: 1,
                                  backgroundColor:
                                    theme?.cardBg ||
                                    (isDarkMode ? "#1e1e1e" : "#fff"),
                                  borderRadius: "12px",
                                  padding: "18px 20px",
                                  border: `2px solid ${
                                    isActive
                                      ? "#3b82f6"
                                      : snapshot.isDragging
                                      ? "#3b82f6"
                                      : theme?.border || "transparent"
                                  }`,
                                  cursor: "pointer",
                                  position: "relative",
                                  transition: "all 0.2s",
                                  boxShadow: snapshot.isDragging
                                    ? "0 10px 20px rgba(0,0,0,0.15)"
                                    : "none",
                                }}
                              >
                                <button
                                  className="no-print"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditClick(acc);
                                  }}
                                  style={{
                                    position: "absolute",
                                    top: "15px",
                                    right: "15px",
                                    background: "none",
                                    border: "none",
                                    color: "#888",
                                    fontSize: "18px",
                                    cursor: "pointer",
                                  }}
                                >
                                  •••
                                </button>

                                <div
                                  style={{
                                    fontSize: "18px",
                                    fontWeight: "900",
                                    color: isActive
                                      ? "#3b82f6"
                                      : theme?.text || "#333",
                                    marginBottom: "15px",
                                    paddingRight: "30px",
                                  }}
                                >
                                  🏨 {acc.name}
                                </div>

                                <div
                                  style={{
                                    display: "flex",
                                    gap: "20px",
                                    flexWrap: "wrap",
                                    marginBottom: "15px",
                                  }}
                                >
                                  <div>
                                    <div
                                      style={{
                                        fontSize: "11px",
                                        color: "#888",
                                        textTransform: "uppercase",
                                        fontWeight: "bold",
                                      }}
                                    >
                                      Check-in
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "14px",
                                        fontWeight: "bold",
                                        color: theme?.text,
                                      }}
                                    >
                                      {formatDisplayDate(acc.checkInDate)}{" "}
                                      <span style={{ color: "#3b82f6" }}>
                                        {acc.checkInTime}
                                      </span>
                                    </div>
                                  </div>
                                  <div
                                    style={{
                                      alignSelf: "center",
                                      color: "#bbb",
                                    }}
                                  >
                                    ➔
                                  </div>
                                  <div>
                                    <div
                                      style={{
                                        fontSize: "11px",
                                        color: "#888",
                                        textTransform: "uppercase",
                                        fontWeight: "bold",
                                      }}
                                    >
                                      Check-out
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "14px",
                                        fontWeight: "bold",
                                        color: theme?.text,
                                      }}
                                    >
                                      {formatDisplayDate(acc.checkOutDate)}{" "}
                                      <span style={{ color: "#ff4757" }}>
                                        {acc.checkOutTime}
                                      </span>
                                    </div>
                                  </div>
                                </div>

                                {/* 🌟 ข้อมูลการติดต่อและ Agency 🌟 */}
                                {(acc.agency || acc.phone || acc.email) && (
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: "15px",
                                      flexWrap: "wrap",
                                      marginBottom: "15px",
                                      padding: "10px",
                                      backgroundColor: isDarkMode
                                        ? "#2c2c2c"
                                        : "#f8f9fa",
                                      borderRadius: "8px",
                                      border: `1px solid ${
                                        isDarkMode ? "#444" : "#eee"
                                      }`,
                                      fontSize: "13px",
                                    }}
                                  >
                                    {acc.agency && (
                                      <div>
                                        <strong style={{ color: "#888" }}>
                                          🏢 จองผ่าน:
                                        </strong>{" "}
                                        {acc.agency}
                                      </div>
                                    )}
                                    {acc.phone && (
                                      <div>
                                        <strong style={{ color: "#888" }}>
                                          📞 เบอร์โทร:
                                        </strong>{" "}
                                        {acc.phone}
                                      </div>
                                    )}
                                    {acc.email && (
                                      <div>
                                        <strong style={{ color: "#888" }}>
                                          📧 อีเมล:
                                        </strong>{" "}
                                        {acc.email}
                                      </div>
                                    )}
                                  </div>
                                )}

                                {(acc.accessCode || acc.guideLink) && (
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: "15px",
                                      flexWrap: "wrap",
                                      marginBottom: "15px",
                                      padding: "10px",
                                      backgroundColor: isDarkMode
                                        ? "#2c2c2c"
                                        : "#f0fdf4",
                                      borderRadius: "8px",
                                      border: `1px solid ${
                                        isDarkMode ? "#444" : "#bbf7d0"
                                      }`,
                                      alignItems: "center",
                                    }}
                                  >
                                    {acc.accessCode && (
                                      <div
                                        style={{ flex: 1, minWidth: "120px" }}
                                      >
                                        <div
                                          style={{
                                            fontSize: "11px",
                                            color: "#888",
                                            fontWeight: "bold",
                                            marginBottom: "4px",
                                          }}
                                        >
                                          🔑 รหัสเข้าพัก (Access Code)
                                        </div>
                                        <div
                                          style={{
                                            fontSize: "16px",
                                            fontWeight: "bold",
                                            color: "#e67e22",
                                          }}
                                        >
                                          {acc.accessCode}
                                        </div>
                                      </div>
                                    )}

                                    {acc.guideLink && (
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "10px",
                                        }}
                                      >
                                        <img
                                          src={`https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(
                                            acc.guideLink
                                          )}`}
                                          alt="QR Code คู่มือที่พัก"
                                          style={{
                                            width: "60px",
                                            height: "60px",
                                            borderRadius: "6px",
                                            padding: "2px",
                                            backgroundColor: "#fff",
                                            border: "1px solid #ddd",
                                          }}
                                        />
                                        <div>
                                          <div
                                            style={{
                                              fontSize: "12px",
                                              fontWeight: "bold",
                                              color: theme?.text,
                                            }}
                                          >
                                            📖 คู่มือที่พัก
                                          </div>
                                          <div
                                            style={{
                                              fontSize: "11px",
                                              color: "#888",
                                              marginTop: "2px",
                                            }}
                                          >
                                            สแกน QR Code เพื่ออ่าน
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}

                                <div
                                  style={{
                                    borderTop: `1px dashed ${
                                      isDarkMode ? "#444" : "#eee"
                                    }`,
                                    paddingTop: "12px",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    flexWrap: "wrap",
                                    gap: "10px",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: "8px",
                                      flexWrap: "wrap",
                                    }}
                                    className="no-print"
                                  >
                                    {acc.googleMap && (
                                      <a
                                        href={acc.googleMap}
                                        target="_blank"
                                        rel="noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        style={{
                                          color: "#2ed573",
                                          textDecoration: "none",
                                          backgroundColor:
                                            "rgba(46, 213, 115, 0.1)",
                                          padding: "4px 8px",
                                          borderRadius: "6px",
                                          fontSize: "12px",
                                          fontWeight: "bold",
                                        }}
                                      >
                                        📍 Map
                                      </a>
                                    )}
                                    {acc.link && (
                                      <a
                                        href={acc.link}
                                        target="_blank"
                                        rel="noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        style={{
                                          color: "#3b82f6",
                                          textDecoration: "none",
                                          backgroundColor:
                                            "rgba(59, 130, 246, 0.1)",
                                          padding: "4px 8px",
                                          borderRadius: "6px",
                                          fontSize: "12px",
                                          fontWeight: "bold",
                                        }}
                                      >
                                        🔗 Booking
                                      </a>
                                    )}
                                    {acc.checkInLink && (
                                      <a
                                        href={acc.checkInLink}
                                        target="_blank"
                                        rel="noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        style={{
                                          color: "#f39c12",
                                          textDecoration: "none",
                                          backgroundColor:
                                            "rgba(243, 156, 18, 0.1)",
                                          padding: "4px 8px",
                                          borderRadius: "6px",
                                          fontSize: "12px",
                                          fontWeight: "bold",
                                        }}
                                      >
                                        📲 E-Ticket
                                      </a>
                                    )}
                                    {acc.guideLink && (
                                      <a
                                        href={acc.guideLink}
                                        target="_blank"
                                        rel="noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        style={{
                                          color: "#9b59b6",
                                          textDecoration: "none",
                                          backgroundColor:
                                            "rgba(155, 89, 182, 0.1)",
                                          padding: "4px 8px",
                                          borderRadius: "6px",
                                          fontSize: "12px",
                                          fontWeight: "bold",
                                        }}
                                      >
                                        📖 Guide Link
                                      </a>
                                    )}
                                  </div>

                                  <div
                                    style={{
                                      textAlign: "right",
                                      marginLeft: "auto",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: "12px",
                                        color: "#888",
                                      }}
                                    >
                                      ราคารวม (Ref: {acc.bookingRef || "-"})
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "18px",
                                        fontWeight: "bold",
                                        color: "#0984e3",
                                      }}
                                    >
                                      ฿
                                      {Number(
                                        acc.totalPrice || 0
                                      ).toLocaleString()}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        {isAdding && (
          <div
            className="no-print"
            style={{
              ...formBgStyle,
              border: `2px dashed #3b82f6`,
              marginTop: "10px",
            }}
          >
            <h4 style={{ margin: "0 0 15px 0", color: "#3b82f6" }}>
              🏨 เพิ่มที่พักใหม่
            </h4>
            <form onSubmit={handleSave}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "15px",
                }}
              >
                <div style={{ display: "flex", gap: "10px", flex: 1 }}>
                  <input
                    type="text"
                    placeholder="ชื่อที่พัก (เช่น APA Hotel Ueno)"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    style={{
                      ...inputStyle,
                      fontWeight: "bold",
                      fontSize: "16px",
                    }}
                    required
                  />
                </div>
              </div>

              <div style={{ display: "flex", flexWrap: "wrap", gap: "20px" }}>
                <div
                  style={{
                    flex: "2 1 400px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "15px",
                  }}
                >
                  <div>
                    <label style={labelStyle}>วันที่ Check-in</label>
                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        alignItems: "center",
                      }}
                    >
                      <input
                        type="date"
                        value={formData.checkInDate}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            checkInDate: e.target.value,
                          })
                        }
                        style={{ ...inputStyle, flex: 1 }}
                        required
                      />
                      <input
                        type="text"
                        placeholder="14:00"
                        maxLength="5"
                        value={formData.checkInTime}
                        onChange={(e) => handleTimeInput(e, "checkInTime")}
                        style={{
                          ...inputStyle,
                          width: "80px",
                          textAlign: "center",
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <label style={labelStyle}>วันที่ Check-out</label>
                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        alignItems: "center",
                      }}
                    >
                      <input
                        type="date"
                        value={formData.checkOutDate}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            checkOutDate: e.target.value,
                          })
                        }
                        style={{ ...inputStyle, flex: 1 }}
                        required
                      />
                      <input
                        type="text"
                        placeholder="11:00"
                        maxLength="5"
                        value={formData.checkOutTime}
                        onChange={(e) => handleTimeInput(e, "checkOutTime")}
                        style={{
                          ...inputStyle,
                          width: "80px",
                          textAlign: "center",
                        }}
                      />
                    </div>
                  </div>

                  <div
                    style={{
                      padding: "10px",
                      backgroundColor: isDarkMode ? "#2c2c2c" : "#eef2f3",
                      borderRadius: "8px",
                      display: "flex",
                      gap: "15px",
                      alignItems: "center",
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <label style={labelStyle}>ราคาต่อคืน (฿)</label>
                      <input
                        type="number"
                        step="any"
                        value={formData.price}
                        onChange={handlePriceChange}
                        style={inputStyle}
                      />
                    </div>
                    <div
                      style={{
                        fontSize: "16px",
                        fontWeight: "bold",
                        color: "#888",
                        marginTop: "15px",
                      }}
                    >
                      x {nights} คืน =
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={labelStyle}>ราคารวมทั้งหมด (฿)</label>
                      <input
                        type="number"
                        step="any"
                        value={formData.totalPrice}
                        onChange={handleTotalPriceChange}
                        style={{
                          ...inputStyle,
                          color: isDarkMode ? "#1dd1a1" : "#0984e3",
                          fontWeight: "bold",
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    flex: "1 1 250px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                    borderLeft: `1px solid ${isDarkMode ? "#444" : "#ddd"}`,
                    paddingLeft: "20px",
                  }}
                >
                  <div>
                    <label style={labelStyle}>
                      หมายเลขยืนยัน (Booking Ref)
                    </label>
                    <input
                      type="text"
                      value={formData.bookingRef}
                      onChange={(e) =>
                        setFormData({ ...formData, bookingRef: e.target.value })
                      }
                      style={inputStyle}
                    />
                  </div>

                  {/* 🌟 ช่องกรอก Agency / โทร / อีเมล */}
                  <div>
                    <label style={labelStyle}>🏢 ช่องทางจอง (Agency)</label>
                    <input
                      type="text"
                      placeholder="เช่น Agoda, Trip.com..."
                      value={formData.agency}
                      onChange={(e) =>
                        setFormData({ ...formData, agency: e.target.value })
                      }
                      style={inputStyle}
                    />
                  </div>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <div style={{ flex: 1 }}>
                      <label style={labelStyle}>📞 เบอร์โทรที่พัก</label>
                      <input
                        type="text"
                        value={formData.phone}
                        onChange={(e) =>
                          setFormData({ ...formData, phone: e.target.value })
                        }
                        style={inputStyle}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={labelStyle}>📧 อีเมลที่พัก</label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) =>
                          setFormData({ ...formData, email: e.target.value })
                        }
                        style={inputStyle}
                      />
                    </div>
                  </div>
                  {/* ---------------------------------- */}

                  <div>
                    <label style={labelStyle}>
                      🔑 รหัสเข้าพัก (Access Code)
                    </label>
                    <input
                      type="text"
                      value={formData.accessCode}
                      onChange={(e) =>
                        setFormData({ ...formData, accessCode: e.target.value })
                      }
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>บันทึกเพิ่มเติม</label>
                    <input
                      type="text"
                      value={formData.notes}
                      onChange={(e) =>
                        setFormData({ ...formData, notes: e.target.value })
                      }
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>
                      🔗 ลิงก์จองที่พัก (Booking Link)
                    </label>
                    <input
                      type="text"
                      placeholder="วางลิงก์ที่นี่..."
                      value={formData.link}
                      onChange={(e) =>
                        setFormData({ ...formData, link: e.target.value })
                      }
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>📍 Google Map Link</label>
                    <input
                      type="text"
                      placeholder="วางลิงก์ที่นี่..."
                      value={formData.googleMap}
                      onChange={(e) =>
                        setFormData({ ...formData, googleMap: e.target.value })
                      }
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>📲 Web Check-in / E-Ticket</label>
                    <input
                      type="text"
                      placeholder="วางลิงก์ที่นี่..."
                      value={formData.checkInLink}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          checkInLink: e.target.value,
                        })
                      }
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>
                      📖 คู่มือที่พัก (Guide Link)
                    </label>
                    <input
                      type="text"
                      placeholder="วางลิงก์ที่นี่..."
                      value={formData.guideLink}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          guideLink: e.target.value,
                        })
                      }
                      style={inputStyle}
                    />
                  </div>
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  marginTop: "20px",
                  justifyContent: "flex-end",
                }}
              >
                <button
                  type="button"
                  onClick={handleCancel}
                  style={{
                    padding: "8px 16px",
                    borderRadius: "8px",
                    border: `1px solid ${isDarkMode ? "#555" : "#ccc"}`,
                    background: isDarkMode ? "#333" : "white",
                    color: isDarkMode ? "#fff" : "#000",
                    cursor: "pointer",
                  }}
                >
                  ยกเลิก
                </button>
                <button
                  type="submit"
                  style={{
                    padding: "8px 24px",
                    borderRadius: "8px",
                    border: "none",
                    background: "#3b82f6",
                    color: "white",
                    fontWeight: "bold",
                    cursor: "pointer",
                  }}
                >
                  บันทึก
                </button>
              </div>
            </form>
          </div>
        )}

        {!isAdding && !editingId && (
          <button
            className="no-print"
            onClick={() => {
              setFormData({
                ...initialFormState,
                checkInDate: trip.start || "",
                checkOutDate: trip.end || "",
              });
              setIsAdding(true);
            }}
            style={{
              padding: "15px",
              borderRadius: "12px",
              border: `1px dashed ${isDarkMode ? "#555" : "#bbb"}`,
              background: "transparent",
              color: isDarkMode ? "#aaa" : "#666",
              fontSize: "15px",
              fontWeight: "bold",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              marginTop: "10px",
              transition: "0.2s",
            }}
          >
            ➕ เพิ่มที่พักใหม่
          </button>
        )}
      </div>

      <div
        className="plan-right-pane no-print"
        style={{
          border: `1px solid ${theme?.border || "#eee"}`,
          borderRadius: "12px",
          overflow: "hidden",
        }}
      >
        <iframe
          title="Accommodation Map"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          loading="lazy"
          allowFullScreen
          src={`https://maps.google.com/maps?q=${encodeURIComponent(
            activeMapQuery
          )}&t=&z=14&ie=UTF8&iwloc=&output=embed`}
        ></iframe>
        <div
          style={{
            position: "absolute",
            top: "15px",
            left: "15px",
            backgroundColor: theme?.cardBg || (isDarkMode ? "#2c2c2c" : "#fff"),
            color: theme?.text || (isDarkMode ? "#fff" : "#000"),
            padding: "8px 15px",
            borderRadius: "20px",
            fontSize: "13px",
            fontWeight: "bold",
            boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
            pointerEvents: "none",
            border: `1px solid ${theme?.border || "#eee"}`,
          }}
        >
          📍 {activeMapQuery}
        </div>
      </div>
    </div>
  );
};

export default AccommodationTab;
